import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, Loader2, CheckCircle2 } from 'lucide-react';
import { BigField, Stepper, TimeInput, bigInput, nowHHMM, uploadAttachment } from './parts';
import { useI18nStore } from '../../../lib/i18n';
const tt = (k, p) => useI18nStore.getState().t(`startStop.${k}`, p);

const todayIso = () => new Date().toISOString().slice(0, 10);

export const initialCustom = (fields) => Object.fromEntries(fields.filter((f) => f.type !== 'photo').map((f) => {
  if (f.type === 'time' && f.default_now) return [f.key, nowHHMM()];
  if (f.type === 'date' && f.default_now) return [f.key, todayIso()];
  return [f.key, ''];
}));

export const missingCustom = (fields, answers, photos) => fields.filter((f) => f.required && (
  f.type === 'photo' ? !photos[f.key] : (answers[f.key] === '' || answers[f.key] == null))).map((f) => f.label);

export const cleanAnswers = (fields, answers) => Object.fromEntries(fields.filter((f) => f.type !== 'photo' && answers[f.key] !== '' && answers[f.key] != null)
  .map((f) => [f.key, f.type === 'number' ? Number(answers[f.key]) : answers[f.key]]));

function PhotoField({ assignmentId, value, onChange, testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    try { onChange(await uploadAttachment(assignmentId, f)); }
    catch (er) { toast.error(er?.response?.data?.detail || tt('photoUploadFailed')); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={testId}
        className={`w-full h-14 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 font-semibold ${value ? 'border-emerald-500 text-emerald-700' : 'border-slate-300 text-slate-600'}`}>
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : value ? <><CheckCircle2 className="h-5 w-5" />{tt('photoAdded')}</> : <><Camera className="h-5 w-5" />{tt('takePhotoFull')}</>}
      </button>
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} />
    </>
  );
}

function Input({ f, value, onChange, testId }) {
  if (f.type === 'number') return <Stepper value={value} onChange={onChange} testId={testId} maxDigits={6} />;
  if (f.type === 'time') return <TimeInput value={value} onChange={onChange} testId={testId} />;
  if (f.type === 'date') return <input type="date" value={value} onChange={(e) => onChange(e.target.value)} className={bigInput} data-testid={`${testId}-input`} />;
  if (f.type === 'boolean') {
    return (
      <div className="grid grid-cols-2 gap-2">
        {[[tt('yes'), true], [tt('no'), false]].map(([l, v]) => (
          <button key={l} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v ? 'yes' : 'no'}`}
            className={`h-14 rounded-xl border-2 text-lg font-bold ${value === v ? 'border-sky-600 bg-sky-600 text-white' : 'border-slate-300'}`}>{l}</button>
        ))}
      </div>
    );
  }
  if (f.type === 'select') {
    return (
      <div className="flex flex-wrap gap-2">
        {(f.options || []).map((o) => (
          <button key={o} type="button" onClick={() => onChange(o)} data-testid={`${testId}-opt-${o}`}
            className={`min-h-12 px-4 rounded-xl border-2 font-semibold ${value === o ? 'border-sky-600 bg-sky-600 text-white' : 'border-slate-300'}`}>{o}</button>
        ))}
      </div>
    );
  }
  return <input type="text" value={value} onChange={(e) => onChange(e.target.value)} maxLength={500} className={`${bigInput} text-lg font-semibold`} data-testid={`${testId}-input`} />;
}

export function CustomFields({ fields, answers, setAnswers, photos, setPhotos, assignmentId, prefix }) {
  return fields.map((f) => (
    <BigField key={f.key} label={`${f.label}${f.required ? ' *' : ` ${tt('optional')}`}`} hint={f.help}>
      {f.type === 'photo'
        ? <PhotoField assignmentId={assignmentId} value={photos[f.key]} onChange={(att) => setPhotos((p) => ({ ...p, [f.key]: att }))} testId={`${prefix}-cf-${f.key}`} />
        : <Input f={f} value={answers[f.key]} onChange={(v) => setAnswers((p) => ({ ...p, [f.key]: v }))} testId={`${prefix}-cf-${f.key}`} />}
    </BigField>
  ));
}
